import React, { useState, useEffect } from "react";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { TemplateWorkspaceManager } from "@/components/template/TemplateWorkspaceManager";
import {
  Maximize,
  Share,
  Edit,
  Menu,
  Settings,
  Eye,
  FileJson,
  PanelLeft,
  PanelLeftClose,
} from "lucide-react";
import {
  useViewerStore,
  useViewerUI,
  useViewerProject,
  useViewerPages,
  useViewerCurrentPageId,
} from "@/stores/viewer/viewer-store";
import { useAuth, useAuthActions } from "@/stores/auth/auth-store";
import { useOrganizationStore } from "@/stores/organization/organization-store";
import { ProjectPermission } from "@/types";
import { Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { ResponsiveAdaptationFramework } from "@/components/mobile/ResponsiveAdaptationFramework";
import {
  useMultiViewportNavigation,
  type NavigationState,
} from "@/hooks/navigation";
import {
  AccessibleNavigation,
  SpatialNavigationProvider,
} from "@/components/navigation";
import { ViewerDebugPanel } from "@/components/debug/ViewerDebugPanel";
import { NavigationDrawer } from "./NavigationDrawer";
import { OrganizationContextSwitcher } from "@/components/organization/OrganizationContextSwitcher";
import { NotificationCenter } from "@/components/notifications/NotificationCenter";
import { logger } from "@/utils/logger";

interface ViewerLayoutProps {
  children: React.ReactNode;
  onOpenSharing?: () => void;
  onOpenSettings?: () => void;
  isTemplateMode?: boolean;
}

interface ViewerHeaderProps {
  onOpenSharing?: () => void;
  onOpenSettings?: () => void;
  navigationState: NavigationState;
  toggleDrawer: () => void;
  isTemplateMode?: boolean;
  sidebarVisible?: boolean;
  onToggleSidebar?: () => void;
  debugPanelOpen?: boolean;
  onToggleDebugPanel?: () => void;
}

const ViewerHeader: React.FC<ViewerHeaderProps> = ({
  onOpenSharing,
  onOpenSettings,
  navigationState,
  toggleDrawer,
  isTemplateMode = false,
  sidebarVisible = false,
  onToggleSidebar,
  debugPanelOpen = false,
  onToggleDebugPanel,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const project = useViewerProject();
  const pages = useViewerPages();
  const currentPageId = useViewerCurrentPageId();
  const { isFullscreen } = useViewerUI();
  const { toggleFullscreen } = useViewerStore();

  // Get user's organizations
  const { organizations } = useOrganizationStore();

  // Check if user has edit permissions for the project
  const canEdit = React.useMemo(() => {
    // If no project is loaded, don't show edit button
    if (!project) {
      return false;
    }

    // If no user is authenticated, don't show edit button
    if (!user) {
      return false;
    }

    // Check if user is the project owner (legacy field)
    const isOwner = project.ownerId === user.id;

    // Check if user is a member of the organization that owns this project
    let isOrgMemberWithEdit = false;
    if (project.owner_type === "Organization" && project.owner_id) {
      const userOrg = organizations.find((org) => org._id === project.owner_id);
      if (userOrg) {
        const orgRole = userOrg.membership?.role;
        isOrgMemberWithEdit = orgRole === "owner" || orgRole === "admin" || orgRole === "member";
      }
    }

    // Check if user is a member with edit permissions
    const isMember = project.members?.some((member) => {
      return (
        member.userId === user.id &&
        (member.permissions.includes(ProjectPermission.WRITE) ||
          member.permissions.includes(ProjectPermission.ADMIN))
      );
    }) || false;

    // For development/demo purposes, also allow if user is authenticated and project is public
    const isDemoAccess = project.visibility === 'public' && user;

    const hasEditPermission = isOwner || isOrgMemberWithEdit || isMember || isDemoAccess;

    return hasEditPermission;
  }, [project, user, organizations]);

  if (isFullscreen) return null;

  return (
    <header className="h-16 border-b bg-background/80 backdrop-blur-sm supports-[backdrop-filter]:bg-background/60">
      <div className="h-full px-4 flex items-center justify-between">
        {/* Left side - Project info and navigation */}
        <div className="flex items-center gap-4">
          {/* Hamburger Menu Button */}
          <button
            onClick={toggleDrawer}
            className="p-2 hover:bg-accent rounded-md transition-colors"
            aria-label="Toggle navigation menu"
            aria-expanded={navigationState.isDrawerOpen}
          >
            <Menu className="h-5 w-5" />
          </button>

          {isTemplateMode ? (
            <>
              <div className="h-6 w-px bg-border" />
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <FileJson className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  <h1 className="text-lg font-semibold">Template Viewer</h1>
                </div>

                {/* Sidebar toggle button */}
                {onToggleSidebar && (
                  <>
                    <div className="h-6 w-px bg-border" />
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={onToggleSidebar}
                            className="gap-2"
                          >
                            {sidebarVisible ? (
                              <PanelLeftClose className="w-4 h-4" />
                            ) : (
                              <PanelLeft className="w-4 h-4" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {sidebarVisible ? "Hide template browser" : "Show template browser"}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </>
                )}
              </div>
            </>
          ) : project ? (
            <>
              <div className="h-6 w-px bg-border" />
              <div className="flex items-center gap-3">
                <div>
                  <h1 className="text-lg font-semibold">{project.name}</h1>
                </div>

                {/* Access type indicator */}
                {project.visibility && (
                  <div className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200">
                    <Eye className="w-3 h-3" />
                    <span className="capitalize">{project.visibility}</span>
                  </div>
                )}

              </div>
            </>
          ) : null}
        </div>

        {/* Right side - Settings dropdown */}
        <div className="flex items-center gap-2">
          {!isTemplateMode && <OrganizationContextSwitcher />}
          <NotificationCenter />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <Settings className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {/* Edit - conditional on canEdit and not template mode */}
              {!isTemplateMode && canEdit && (
                <DropdownMenuItem onClick={() => navigate(`/editor/${project?.id}${currentPageId ? `/page/${currentPageId}` : ''}`)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </DropdownMenuItem>
              )}

              {/* Debug */}
              {onToggleDebugPanel && (
                <DropdownMenuItem onClick={onToggleDebugPanel}>
                  <FileJson className="w-4 h-4 mr-2" />
                  Debug
                  {debugPanelOpen && <span className="ml-auto text-xs">✓</span>}
                </DropdownMenuItem>
              )}

              {/* Share - disabled */}
              {!isTemplateMode && (
                <DropdownMenuItem disabled>
                  <Share className="w-4 h-4 mr-2" />
                  Share
                </DropdownMenuItem>
              )}

              <DropdownMenuSeparator />

              {/* Fullscreen */}
              <DropdownMenuItem onClick={toggleFullscreen}>
                <Maximize className="w-4 h-4 mr-2" />
                Fullscreen
              </DropdownMenuItem>

              {/* Settings - disabled */}
              {!isTemplateMode && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem disabled>
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};


export const ViewerLayout: React.FC<ViewerLayoutProps> = ({
  children,
  onOpenSharing,
  onOpenSettings,
  isTemplateMode = false,
}) => {
  const { isFullscreen } = useViewerUI();
  const { navigationState, toggleDrawer, closeDrawer } = useMultiViewportNavigation();
  const { logout } = useAuthActions();
  const navigate = useNavigate();

  const handleSignOut = () => {
    logout();
    navigate("/");
  };

  const [mounted, setMounted] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [debugPanelOpen, setDebugPanelOpen] = useState(false);
  const layoutRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
    // Load sidebar visibility state from localStorage
    if (isTemplateMode) {
      try {
        const saved = localStorage.getItem("terramatrix-template-sidebar-visible");
        if (saved) {
          const raw: unknown = JSON.parse(saved);
          const result = z.boolean().safeParse(raw);
          if (result.success) {
            setSidebarVisible(result.data);
          }
        }
      } catch (err) {
        console.error("Failed to load sidebar visibility state:", err);
      }
    }
  }, [isTemplateMode]);

  // Save sidebar visibility state to localStorage
  const handleToggleSidebar = () => {
    const newVisible = !sidebarVisible;
    setSidebarVisible(newVisible);
    try {
      localStorage.setItem("terramatrix-template-sidebar-visible", JSON.stringify(newVisible));
    } catch (err) {
      console.error("Failed to save sidebar visibility state:", err);
    }
  };

  // Handle fullscreen mode
  useEffect(() => {
    if (!mounted) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "F11" || (e.key === "f" && e.ctrlKey)) {
        e.preventDefault();
        useViewerStore.getState().toggleFullscreen();
      }
      if (e.key === "Escape" && isFullscreen) {
        useViewerStore.getState().toggleFullscreen();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isFullscreen, mounted]);

  // Debug fullscreen state changes
  useEffect(() => {
    logger.debug("Viewer fullscreen state changed", { isFullscreen });

    if (layoutRef.current && isFullscreen) {
      const layout = layoutRef.current;
      setTimeout(() => {
        logger.debug("Viewer fullscreen container dimensions", {
          scrollHeight: layout.scrollHeight,
          clientHeight: layout.clientHeight,
          canScroll: layout.scrollHeight > layout.clientHeight,
          overflow: window.getComputedStyle(layout).overflow,
          overflowY: window.getComputedStyle(layout).overflowY,
          height: window.getComputedStyle(layout).height,
          position: window.getComputedStyle(layout).position,
          classes: layout.className,
        });
      }, 100);
    }
  }, [isFullscreen]);

  if (!mounted) {
    return null;
  }

  return (
    <SpatialNavigationProvider
      config={{ enabled: true, focusRingVisible: true }}
    >
      <AccessibleNavigation>
        <TooltipProvider>
          <div
            ref={layoutRef}
            className={cn(
              "h-screen bg-background flex flex-col",
              isFullscreen && "fixed inset-0 z-50 overflow-y-auto overflow-x-hidden",
            )}
          >
        <ViewerHeader
          onOpenSharing={onOpenSharing}
          onOpenSettings={onOpenSettings}
          navigationState={navigationState}
          toggleDrawer={toggleDrawer}
          isTemplateMode={isTemplateMode}
          sidebarVisible={sidebarVisible}
          onToggleSidebar={handleToggleSidebar}
          debugPanelOpen={debugPanelOpen}
          onToggleDebugPanel={() => setDebugPanelOpen(!debugPanelOpen)}
        />

        {/* Navigation Drawer */}
        <NavigationDrawer
          isOpen={navigationState.isDrawerOpen}
          onClose={closeDrawer}
          onSignOut={handleSignOut}
        />

        {/* Main content area with template workspace sidebar */}
        <div className="flex-1 flex min-h-0">
          {/* Template workspace sidebar - completely hidden when not visible */}
          {isTemplateMode && (
            <div className={cn(
              "relative z-20 flex-shrink-0 transition-all duration-300 overflow-hidden",
              sidebarVisible ? "w-64" : "w-0"
            )}>
              {sidebarVisible && <TemplateWorkspaceManager />}
            </div>
          )}

          {/* Main content - flex-1 fills remaining space, flex ensures children can use flex-1 */}
          <main className="flex-1 flex flex-col relative z-0 min-h-0 min-w-0 overflow-hidden">
            <ResponsiveAdaptationFramework>
              {children}
            </ResponsiveAdaptationFramework>
          </main>

        </div>

        {/* Debug Panel */}
        <ViewerDebugPanel
          isOpen={debugPanelOpen}
          onClose={() => setDebugPanelOpen(false)}
        />
          </div>
        </TooltipProvider>
      </AccessibleNavigation>
    </SpatialNavigationProvider>
  );
};
